import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDrop } from "react-dnd";
import { KnowledgeFolder } from "../../models/API";

/** Returns icon color based on folder visibility */
function getFolderIconColor(folder: KnowledgeFolder): string {
  if (folder.is_global) return "#ed6c02"; // orange
  if (folder.is_public) return "#2e7d32"; // green
  return "#1565c0"; // blue
}

export const KNOWLEDGE_CARD = "knowledge_card";

export interface DragItem {
  id: number;
  folder_id: number | null;
}

interface FolderSidebarProps {
  folders: KnowledgeFolder[];
  selectedFolderId: number | null | "all";
  onSelectFolder: (folderId: number | null | "all") => void;
  onCreateFolder: () => void;
  onRenameFolder: (folder: KnowledgeFolder) => void;
  onDeleteFolder: (folderId: number) => void;
  onMoveItem: (itemId: number, targetFolderId: number | null) => void;
  effectiveRole: string;
  currentUserId: number;
  globalTeamId: number;
}

function canEditFolder(
  folder: KnowledgeFolder,
  effectiveRole: string,
  currentUserId: number,
  globalTeamId: number
): boolean {
  const isOperatorOrAdmin =
    effectiveRole === "admin" || effectiveRole === "operator";
  if (folder.is_global) {
    return isOperatorOrAdmin && globalTeamId === 1;
  }
  if (folder.is_public) {
    return isOperatorOrAdmin;
  }
  return folder.creator_id === currentUserId;
}

interface DroppableFolderItemProps {
  folder: KnowledgeFolder;
  isSelected: boolean;
  onSelect: () => void;
  onContextMenu: (event: React.MouseEvent, folder: KnowledgeFolder) => void;
  onMoveItem: (itemId: number, targetFolderId: number | null) => void;
  canEdit: boolean;
}

function DroppableFolderItem({
  folder,
  isSelected,
  onSelect,
  onContextMenu,
  onMoveItem,
  canEdit,
}: DroppableFolderItemProps) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: KNOWLEDGE_CARD,
      drop: (item: DragItem) => {
        onMoveItem(item.id, folder.id);
      },
      canDrop: (item: DragItem) => item.folder_id !== folder.id,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [folder.id, onMoveItem]
  );

  const iconColor = getFolderIconColor(folder);

  return (
    <ListItemButton
      ref={drop}
      selected={isSelected}
      onClick={onSelect}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        py: 0.75,
        transition: "all 0.2s ease",
        backgroundColor: isOver && canDrop ? "action.hover" : undefined,
        border: isOver && canDrop ? "2px dashed" : "2px solid transparent",
        borderColor: isOver && canDrop ? "primary.main" : "transparent",
        "&:hover": {
          backgroundColor: "action.hover",
          transform: "translateX(2px)",
        },
        "&.Mui-selected": {
          backgroundColor: "primary.50",
          borderLeft: `3px solid ${iconColor}`,
          "&:hover": {
            backgroundColor: "primary.100",
          },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        {isSelected ? (
          <FolderOpenIcon sx={{ color: iconColor, fontSize: 20 }} />
        ) : (
          <FolderIcon sx={{ color: iconColor, fontSize: 20 }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={folder.folder_name}
        primaryTypographyProps={{
          noWrap: true,
          variant: "body2",
          fontWeight: isSelected ? 600 : 400,
        }}
      />
      <Chip
        label={folder.item_count}
        size="small"
        sx={{
          mr: 0.5,
          minWidth: 24,
          height: 20,
          fontSize: "0.7rem",
          fontWeight: 600,
          backgroundColor: isSelected ? iconColor : "grey.200",
          color: isSelected ? "#fff" : "text.secondary",
          borderRadius: "10px",
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
      {canEdit && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onContextMenu(e, folder);
          }}
          sx={{
            opacity: 0.5,
            transition: "opacity 0.2s",
            "&:hover": { opacity: 1 },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
    </ListItemButton>
  );
}

export default function FolderSidebar({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveItem,
  effectiveRole,
  currentUserId,
  globalTeamId,
}: FolderSidebarProps) {
  const [contextMenu, setContextMenu] = useState<{
    anchorEl: HTMLElement;
    folder: KnowledgeFolder;
  } | null>(null);

  const handleContextMenu = (
    event: React.MouseEvent,
    folder: KnowledgeFolder
  ) => {
    setContextMenu({ anchorEl: event.currentTarget as HTMLElement, folder });
  };

  const handleCloseMenu = () => {
    setContextMenu(null);
  };

  return (
    <Box
      sx={{
        width: 260,
        minWidth: 260,
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: (t) =>
          t.palette.mode === "dark" ? t.palette.background.default : "grey.50",
        boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="text.secondary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.7rem",
            mb: 1.5,
          }}
        >
          Folders
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CreateNewFolderIcon />}
          onClick={onCreateFolder}
          size="small"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "primary.200",
            color: "primary.main",
            backgroundColor: (t) =>
              t.palette.mode === "dark" ? t.palette.background.paper : "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            "&:hover": {
              backgroundColor: "primary.50",
              borderColor: "primary.main",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            },
          }}
        >
          New Folder
        </Button>
      </Box>
      <Divider sx={{ mx: 1.5 }} />
      <Box sx={{ flexGrow: 1, overflow: "auto", p: 1.5, pt: 1 }}>
        <List dense disablePadding>
          <ListItemButton
            selected={selectedFolderId === "all"}
            onClick={() => onSelectFolder("all")}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              py: 0.75,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "action.hover",
                transform: "translateX(2px)",
              },
              "&.Mui-selected": {
                backgroundColor: "primary.50",
                borderLeft: "3px solid",
                borderLeftColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.100",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <AllInboxIcon
                fontSize="small"
                color={selectedFolderId === "all" ? "primary" : "action"}
              />
            </ListItemIcon>
            <ListItemText
              primary="All Items"
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: selectedFolderId === "all" ? 600 : 400,
              }}
            />
          </ListItemButton>

          {folders.map((folder) => (
            <DroppableFolderItem
              key={folder.id}
              folder={folder}
              isSelected={selectedFolderId === folder.id}
              onSelect={() => onSelectFolder(folder.id)}
              onContextMenu={handleContextMenu}
              onMoveItem={onMoveItem}
              canEdit={canEditFolder(
                folder,
                effectiveRole,
                currentUserId,
                globalTeamId
              )}
            />
          ))}
        </List>
      </Box>

      <Menu
        anchorEl={contextMenu?.anchorEl}
        open={!!contextMenu}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.12)" },
        }}
      >
        <MenuItem
          onClick={() => {
            if (contextMenu) onRenameFolder(contextMenu.folder);
            handleCloseMenu();
          }}
          sx={{ borderRadius: 1, mx: 0.5 }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (contextMenu) onDeleteFolder(contextMenu.folder.id);
            handleCloseMenu();
          }}
          sx={{ borderRadius: 1, mx: 0.5 }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

export { canEditFolder };
